<script setup lang="ts">
  import { useThrottleFn } from '@vueuse/core'

  import { ChevronIcon } from '@/shared/ui/svg'

  import type { BaseDropdownOption } from '../model'

  type DropdownDirection = 'down' | 'up'

  interface Props {
    placeholder?: string
    disabled?: boolean
    outlined?: boolean
    options?: BaseDropdownOption[]
    /**
     * Направление открытия по умолчанию.
     * Если места не хватает — откроется в обратную сторону.
     */
    defaultDirection?: DropdownDirection

    /**
     * Контейнер, относительно которого проверяем "влезает/не влезает".
     * По умолчанию считаем относительно окна (viewport).
     *
     * Можно передать:
     * - HTMLElement (например, ref на контейнер)
     * - строку-селектор (например ".modal-body")
     */
    parentContainer?: HTMLElement | string | null
  }

  interface Emits {
    (e: 'select', data: string | undefined): void
  }

  const props = withDefaults(defineProps<Props>(), {
    defaultDirection: 'down',
    parentContainer: null,
  })

  const emit = defineEmits<Emits>()
  const localValue = defineModel<string | undefined>({ default: '' })

  const menuIsActive = ref(false)

  const dropdownRef = shallowRef<HTMLElement>()
  const menuRef = shallowRef<HTMLElement>()

  const selectedValueItem = computed(() => {
    if (!props.options?.length) {
      return undefined
    }
    if (!localValue.value) {
      return undefined
    }
    return props.options.find((option) => option.value === localValue.value)
  })

  const dropdownNameString = computed(() => {
    return selectedValueItem.value?.label || props.placeholder || ''
  })

  const handleSelect = (newValue: string | undefined) => {
    localValue.value = newValue
    emit('select', newValue)
    menuIsActive.value = false
  }

  onClickOutside(dropdownRef, () => (menuIsActive.value = false))

  const openedDirection = ref<DropdownDirection>(props.defaultDirection)

  const oppositeDirection = (direction: DropdownDirection): DropdownDirection => (direction === 'down' ? 'up' : 'down')

  const parentEl = shallowRef<HTMLElement | null>(null)

  const resolveParent = () => {
    if (!props.parentContainer) {
      parentEl.value = null
      return
    }
    if (typeof props.parentContainer === 'string') {
      parentEl.value = document.querySelector(props.parentContainer) as HTMLElement | null
      return
    }
    parentEl.value = props.parentContainer
  }

  const getBounds = () => {
    if (!parentEl.value) {
      return { top: 0, bottom: window.innerHeight }
    }
    const parentRect = parentEl.value.getBoundingClientRect()
    return { top: parentRect.top, bottom: parentRect.bottom }
  }

  const updateDirection = () => {
    if (!menuIsActive.value) {
      return
    }

    if (!dropdownRef.value || !menuRef.value) {
      return
    }

    const triggerRect = dropdownRef.value.getBoundingClientRect()
    const menuHeight = menuRef.value.scrollHeight

    const bounds = getBounds()
    const spaceBelow = bounds.bottom - triggerRect.bottom
    const spaceAbove = triggerRect.top - bounds.top

    const preferred = props.defaultDirection
    const opposite = oppositeDirection(preferred)

    const preferredSpace = preferred === 'down' ? spaceBelow : spaceAbove
    openedDirection.value = preferredSpace >= menuHeight ? preferred : opposite
  }

  watch(
    () => props.defaultDirection,
    () => {
      if (menuIsActive.value) {
        nextTick(updateDirection)
      } else {
        openedDirection.value = props.defaultDirection
      }
    },
  )

  const toggleMenu = async () => {
    if (props.disabled) {
      return
    }

    menuIsActive.value = !menuIsActive.value

    if (menuIsActive.value) {
      nextTick(updateDirection)
    }
  }

  const onViewportChange = useThrottleFn(updateDirection, 100)

  let unbindListeners: (() => void) | null = null

  const bindListeners = () => {
    unbindListeners?.()

    const scrollTarget: HTMLElement | Window = parentEl.value ?? window

    scrollTarget.addEventListener('scroll', onViewportChange, { passive: true, capture: true })
    window.addEventListener('resize', onViewportChange, { passive: true })

    unbindListeners = () => {
      scrollTarget.removeEventListener('scroll', onViewportChange, { capture: true })
      window.removeEventListener('resize', onViewportChange)
      unbindListeners = null
    }
  }

  watch(menuIsActive, (isActive) => {
    if (isActive) {
      bindListeners()
    } else {
      unbindListeners?.()
    }
  })

  watch(
    () => props.parentContainer,
    () => {
      resolveParent()
      if (menuIsActive.value) {
        updateDirection()
        bindListeners()
      }
    },
  )

  onMounted(resolveParent)

  onBeforeUnmount(() => {
    unbindListeners?.()
  })

  const transitionName = computed(() => (openedDirection.value === 'down' ? 'slide-bottom' : 'slide-top'))
</script>

<template>
  <div
    ref="dropdownRef"
    class="dropdown__container relative flex min-w-30 shrink-0 cursor-pointer items-stretch justify-start gap-3.5"
    :class="[
      {
        opened: menuIsActive,
        outlined,
        disabled,
      },
    ]"
    @click="toggleMenu"
  >
    <div
      class="dropdown__name z-3 flex max-h-full w-full shrink-0 flex-nowrap! items-center justify-between gap-1 rounded-lg border py-3 pr-4 pl-6"
      :class="{
        'border-transparent': !outlined,
      }"
    >
      <div
        class="dropdown__name-value"
        :class="{
          placeholder: !selectedValueItem,
        }"
      >
        <component
          :is="selectedValueItem?.renderLabel"
          v-if="selectedValueItem?.renderLabel"
        />
        <template v-else>
          {{ dropdownNameString }}
        </template>
      </div>

      <ChevronIcon
        class="dropdown__arrow shrink-0"
        :class="{ 'rotate-90': menuIsActive }"
      />
    </div>

    <Transition :name="transitionName">
      <div
        v-if="menuIsActive"
        ref="menuRef"
        class="dropdown__menu absolute inset-x-0 z-4 flex w-full flex-col items-start justify-start divide-y overflow-hidden rounded"
        :class="openedDirection === 'down' ? 'dropdown__menu--down' : 'dropdown__menu--up'"
      >
        <div
          v-for="(option, index) in options"
          :key="`${option.value}_${index}`"
          class="dropdown__menu-item w-full px-3 py-2.5"
          :class="{
            selected: option.value === selectedValueItem?.value,
          }"
          @click.stop="handleSelect(option.value)"
        >
          <component
            :is="option.renderLabel"
            v-if="option.renderLabel"
          />
          <template v-else>
            {{ option.label }}
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
  .dropdown {
    &__container {
      &.opened,
      &:not(.disabled):hover {
        .dropdown {
          &__name-value {
            color: var(--component-base-dropdown-name-color-hover);
          }
          &__arrow {
            color: var(--component-base-dropdown-icon-color-hover);
          }
          &__name {
            border-color: var(--component-base-dropdown-name-border-color-hover);
          }
        }
      }
      &.outlined {
        .dropdown {
          &__name {
            border-color: var(--component-base-dropdown-outlined-name-border-color);
          }
        }
      }
    }

    &__name {
      @include font-montserrat-500(14px, 17px);
      transition: all 0.2s ease;
      background-color: var(--component-base-dropdown-name-background-color);
    }

    &__name-value {
      color: var(--component-base-dropdown-name-color);
      &.placeholder {
        color: var(--text-secondary);
      }
    }

    &__menu {
      transition: all 0.2s ease;
      background-color: var(--component-base-dropdown-menu-background-color);
      box-shadow: 2px 4px 5px 0px var(--component-base-dropdown-box-shadow-color);
      border: 1px solid var(--border-border-secondary);
      border-radius: 8px;

      &--down {
        top: 100%;
        margin-top: 10px;
        transform-origin: top;
      }

      &--up {
        bottom: 100%;
        margin-bottom: 10px;
        transform-origin: bottom;
      }
    }

    &__menu-item {
      @include font-montserrat-400(14px, 24px, var(--component-base-dropdown-menu-item-color));
      transition: all 0.2s ease;
      border-color: var(--component-base-dropdown-menu-item-border-color);

      &.selected {
        background-color: var(--component-base-dropdown-menu-item-selected-background-color);
        color: var(--component-base-dropdown-menu-item-selected-color);
      }

      &:hover {
        background-color: var(--component-base-dropdown-menu-item-background-color-hover);
      }
    }

    &__arrow {
      color: var(--component-base-dropdown-icon-color);
      transition: all 0.2s ease;
    }
  }

  .slide-bottom-pseudo-enter-from,
  .slide-bottom-pseudo-leave-to {
    opacity: 0;
  }

  .slide-bottom-enter-from,
  .slide-bottom-leave-to {
    transform: scaleY(0);
    opacity: 0;
  }

  .slide-top-pseudo-enter-from,
  .slide-top-pseudo-leave-to {
    opacity: 0;
  }

  .slide-top-enter-from,
  .slide-top-leave-to {
    transform: scaleY(0);
    opacity: 0;
  }
</style>
